import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { Download } from "lucide-react";
import { Card, PageHeader, Btn, Field, inputCls } from "../../components/dash/Kit";
import { api, errMessage } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { COMPANY } from "../../data/legal";

const notificationKeys = [
  ["content_ready", "Content Ready"],
  ["revision_complete", "Revision Complete"],
  ["new_invoice", "New Invoice"],
  ["subscription_update", "Subscription Update"],
  ["promotion_reminder", "Promotion Reminder"],
  ["posting_reminder", "Posting Reminders"],
  ["account_updates", "Important Account Updates"],
];

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", business_name: "", email: "", phone: "", language: "en" });
  const [notifications, setNotifications] = useState({});
  const [marketing, setMarketing] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || "", last_name: user.last_name || "",
      business_name: user.business_name || "",
      email: user.email || "", phone: user.phone || "", language: user.language || "en",
    });
    setNotifications(user.notifications || {});
    setMarketing(!!user.marketing_opt_in);
  }, [user]);

  const saveProfile = async () => {
    const n = {};
    if (!form.first_name.trim()) n.first_name = "Please enter your first name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) n.email = "Please enter a valid email address.";
    setErrors(n);
    if (Object.keys(n).length) return;
    try {
      await api.put("/me", { ...form, notifications, marketing_opt_in: marketing });
      toast.success("Settings saved.");
      refresh();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const savePassword = async () => {
    const n = {};
    if (!pw.current_password) n.current_password = "Please enter your current password.";
    if (pw.new_password.length < 8) n.new_password = "Please use at least 8 characters.";
    if (pw.new_password !== pw.confirm) n.confirm = "Passwords do not match.";
    setErrors(n);
    if (Object.keys(n).length) return;
    try {
      await api.put("/auth/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated.");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      setErrors({ current_password: errMessage(e) });
      toast.error(errMessage(e));
    }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `postmint-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/me");
      toast.success("Your account has been deleted.");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader title="Account Settings" body="Update your details, preferences and password." />

      <Card testId="settings-profile-card">
        <p className="font-display text-lg font-semibold text-slate-900">Your details</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <Field label="First Name" error={errors.first_name} htmlFor="s-first">
            <input id="s-first" data-testid="settings-first-name-input" className={inputCls} value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} />
          </Field>
          <Field label="Last Name" htmlFor="s-last">
            <input id="s-last" data-testid="settings-last-name-input" className={inputCls} value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} />
          </Field>
          <Field label="Business Name" htmlFor="s-business">
            <input id="s-business" data-testid="settings-business-name-input" className={inputCls} value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} />
          </Field>
          <Field label="Email" error={errors.email} htmlFor="s-email">
            <input id="s-email" type="email" data-testid="settings-email-input" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label="Phone" htmlFor="s-phone">
            <input id="s-phone" data-testid="settings-phone-input" className={inputCls} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </Field>
          <Field label="Language">
            <select data-testid="settings-language-select" className={inputCls} value={form.language} onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
            </select>
          </Field>
        </div>

        <p className="mt-7 font-display text-base font-semibold text-slate-900">Notification Preferences</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {notificationKeys.map(([key, label]) => (
            <label key={key} htmlFor={`notif-${key}`} className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700">
              <input
                id={`notif-${key}`}
                type="checkbox"
                data-testid={`notification-toggle-${key}`}
                className="h-4 w-4 accent-[#F95738]"
                checked={!!notifications[key]}
                onChange={(e) => setNotifications((n) => ({ ...n, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>

        <label htmlFor="marketing-opt-in" className="mt-5 flex items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
          <input
            id="marketing-opt-in"
            type="checkbox"
            data-testid="marketing-opt-in-checkbox"
            className="mt-1 h-4 w-4 accent-[#F95738]"
            checked={marketing}
            onChange={(e) => setMarketing(e.target.checked)}
          />
          <span>
            <span className="block font-semibold text-slate-900">Marketing Preferences</span>
            Yes, send me occasional PostMint tips and offers by email. This consent is separate from account emails and
            you can withdraw it at any time.
          </span>
        </label>

        <Btn testId="settings-save-button" className="mt-6" onClick={saveProfile}>Save Changes</Btn>
      </Card>

      <Card testId="settings-password-card">
        <p className="font-display text-lg font-semibold text-slate-900">Change password</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          <Field label="Current Password" error={errors.current_password} htmlFor="pw-current">
            <input id="pw-current" type="password" data-testid="current-password-input" className={inputCls} value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} />
          </Field>
          <Field label="New Password" error={errors.new_password} htmlFor="pw-new">
            <input id="pw-new" type="password" data-testid="new-password-input" className={inputCls} value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} />
          </Field>
          <Field label="Confirm New Password" error={errors.confirm} htmlFor="pw-confirm">
            <input id="pw-confirm" type="password" data-testid="confirm-password-input" className={inputCls} value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />
          </Field>
        </div>
        <Btn testId="save-password-button" className="mt-6" onClick={savePassword}>Update Password</Btn>
      </Card>

      <Card testId="settings-privacy-card">
        <p className="font-display text-lg font-semibold text-slate-900">Privacy &amp; Data</p>
        <p className="mt-2 text-sm text-slate-600">
          Your GDPR rights, in one place. You can act on most of them yourself right here.
        </p>

        <div className="mt-5 space-y-3">
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-900">Download your data</p>
            <p className="mt-1 text-sm text-slate-600">
              Get a machine-readable JSON file with your account, Brand Profile, content, calendar, invoices and support
              history. Uploaded originals stay downloadable from Media.
            </p>
            <Btn variant="outline" className="mt-3" testId="export-data-button" onClick={exportData}>
              <Download className="h-4 w-4" /> Download my data
            </Btn>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-900">Correct your data</p>
            <p className="mt-1 text-sm text-slate-600">
              Edit your details above, or in Brand Profile. If something can't be corrected in the app, email{" "}
              <a href={`mailto:${COMPANY.email}`} className="font-semibold text-[#F95738] hover:underline">
                {COMPANY.email}
              </a>{" "}
              and we'll correct it for you.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-900">Restrict, object or withdraw consent</p>
            <p className="mt-1 text-sm text-slate-600">
              Untick marketing above to withdraw marketing consent. To restrict or object to other processing, email{" "}
              {COMPANY.email}. Cookie choices can be changed with “Cookie settings” in the website footer.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-900">What happens when you delete your account</p>
            <p className="mt-1 text-sm text-slate-600">
              Your Brand Profile, uploads, generated content, calendar, connections and support history are removed from
              our live systems. Access tokens for connected social accounts are deleted, so nothing can be published.
              Records we must keep by law — such as invoices for tax purposes — are retained for the statutory period,
              and backups fall away on their own cycle. Deletion cannot be undone.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Retention wording is configurable in our{" "}
              <Link to="/legal/privacy-policy" className="font-semibold text-[#F95738] hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-900">Contact privacy support</p>
            <p className="mt-1 text-sm text-slate-600">
              Email{" "}
              <a href={`mailto:${COMPANY.email}`} className="font-semibold text-[#F95738] hover:underline">
                {COMPANY.email}
              </a>{" "}
              for any data request. You can also complain to the Dutch Data Protection Authority (Autoriteit
              Persoonsgegevens) or your local supervisory authority.
            </p>
          </div>
        </div>
      </Card>

      <Card testId="settings-delete-card">
        <p className="font-display text-lg font-semibold text-slate-900">Account Deletion</p>
        <p className="mt-2 text-sm text-slate-600">
          Deleting your account permanently removes your Brand Profile, uploads, promotions and content history.
        </p>
        {confirmDelete ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Btn variant="danger" testId="delete-account-confirm" onClick={deleteAccount}>Yes, delete my account</Btn>
            <Btn variant="outline" testId="delete-account-cancel" onClick={() => setConfirmDelete(false)}>Cancel</Btn>
          </div>
        ) : (
          <Btn variant="danger" testId="delete-account-button" className="mt-4" onClick={() => setConfirmDelete(true)}>
            Delete My Account
          </Btn>
        )}
      </Card>
    </div>
  );
}
